import React, { useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import "react-big-calendar/lib/css/react-big-calendar.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import moment from 'moment/moment';
import styled from 'styled-components';
import AppModal from './AppModal';

const DnDCalendar = withDragAndDrop(Calendar);
const localizer = momentLocalizer(moment);

const CalendarContainer = styled.div`
    height: 600px;
    margin: 2rem 0;
    background: white;
    padding: 1rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const TourCard = styled.div`
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    margin-bottom: 1rem;
`;

const TourHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
`;

const TourTitle = styled.h3`
    margin: 0;
    color: #333;
    font-size: 1.2rem;
`;

const TourDetails = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
`;

const DetailLabel = styled.span`
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 0.25rem;
`;

const DetailValue = styled.span`
    font-weight: 500;
    color: #333;
`;

const Legend = styled.div`
    display: flex;
    gap: 1.5rem;
    margin-top: -1rem;
    margin-bottom: 2rem;
    padding: 0.75rem 1rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    font-size: 0.9rem;
    color: #555;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const LegendSwatch = styled.span<{ $color: string; $dashed?: boolean }>`
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 4px;
    background-color: ${props => props.$color};
    border: 2px ${props => props.$dashed ? 'dashed' : 'solid'} ${props => props.$dashed ? '#e65100' : props.$color};
`;

const getEventTitle = (tour: any) => {
    const name = tour.title || tour.tourTitle;
    if (tour.isConfirmedByGuide === false) {
        return `[Awaiting] ${name}`;
    }
    return name;
};

const getEventStyle = (tour: any) => {
    if (tour.isConfirmedByGuide === true) {
        return {
            backgroundColor: '#4CAF50',
            border: '2px solid #388E3C',
            borderRadius: '4px',
            color: 'white'
        };
    }
    if (tour.isConfirmedByGuide === false) {
        return {
            backgroundColor: '#FFF3E0',
            border: '2px dashed #FF9800',
            borderRadius: '4px',
            color: '#E65100'
        };
    }
    return {
        backgroundColor: '#90A4AE',
        border: '2px solid #78909C',
        borderRadius: '4px',
        color: 'white'
    };
};

interface TourCalendarProps {
    tours: any[];
    renderTourCard?: (tour: any, options?: { closeModal: () => void }) => React.ReactNode;
    onMonthChange?: (date: Date) => void;
    currentDate?: Date;
    showConfirmationLegend?: boolean;
}

const TourCalendar: React.FC<TourCalendarProps> = ({ tours, renderTourCard, onMonthChange, currentDate, showConfirmationLegend }) => {
    const [selectedTour, setSelectedTour] = useState<any>(null);
    const [showTourModal, setShowTourModal] = useState(false);
    /** When parent does not pass currentDate, calendar must own visible month or `moment()` pins view to "today" every render. */
    const [internalDate, setInternalDate] = useState<Date>(() => new Date());

    const events = tours.map((tour: any) => ({
        id: tour._id,
        title: getEventTitle(tour),
        start: moment(tour.bookingDate).toDate(),
        end: moment(tour.bookingDate).add(1, 'hour').toDate(),
        resource: tour
    }));

    const handleSelectEvent = (event: any) => {
        setSelectedTour(event.resource);
        setShowTourModal(true);
    };

    const handleNavigate = (newDate: Date) => {
        if (onMonthChange) {
            onMonthChange(newDate);
        }
        if (currentDate === undefined) {
            setInternalDate(newDate);
        }
    };

    const calendarDate = currentDate ?? internalDate;

    const defaultRenderTourCard = (tour: any) => {
        const totals = {
            adults: 0,
            youths: 0,
            children: 0,
            enfants: 0
        };

        if (tour?.bookings) {
            tour.bookings.forEach((booking: any) => {
                if (booking?.client) {
                    totals.adults += booking.client.clientAdult || 0;
                    totals.youths += booking.client.clientYouth || 0;
                    totals.children += booking.client.clientChild || 0;
                    totals.enfants += booking.client.clientEnfant || 0;
                }
            });
        }

        return (
            <TourCard>
                <TourHeader>
                    <TourTitle>{tour.title || tour.tourTitle}</TourTitle>
                </TourHeader>
                <TourDetails>
                    <DetailItem>
                        <DetailLabel>Date</DetailLabel>
                        <DetailValue>{moment(tour.bookingDate).format("D MMM YYYY h:mm a")}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Language</DetailLabel>
                        <DetailValue>{tour.language ?? 'Not set'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Customers</DetailLabel>
                        <DetailValue>{tour.totalCustomers || 0}</DetailValue>
                    </DetailItem>
                    {tour.bookings && (
                        <>
                            <DetailItem>
                                <DetailLabel>Total Adults</DetailLabel>
                                <DetailValue>{totals.adults}</DetailValue>
                            </DetailItem>
                            <DetailItem>
                                <DetailLabel>Total Youths</DetailLabel>
                                <DetailValue>{totals.youths}</DetailValue>
                            </DetailItem>
                            <DetailItem>
                                <DetailLabel>Total Children</DetailLabel>
                                <DetailValue>{totals.children}</DetailValue>
                            </DetailItem>
                            <DetailItem>
                                <DetailLabel>Total Enfants</DetailLabel>
                                <DetailValue>{totals.enfants}</DetailValue>
                            </DetailItem>
                        </>
                    )}
                    {tour.guide && tour.guide.length > 0 && (
                        <DetailItem>
                            <DetailLabel>Guides</DetailLabel>
                            <DetailValue>
                                {tour.guide.map((g: any) => {
                                    const name = `${g.profile.firstName} ${g.profile.lastName}`;
                                    const status = g.isConfirmedByGuide === true
                                        ? `Confirmed${g.isConfirmedDate ? ` (${moment(g.isConfirmedDate).format('MMM D, YYYY')})` : ''}`
                                        : 'Not confirmed';
                                    return `${name} — ${status}`;
                                }).join('; ')}
                            </DetailValue>
                        </DetailItem>
                    )}
                    {(!tour.guide || tour.guide.length === 0) && tour.isConfirmedByGuide !== undefined && (
                        <DetailItem>
                            <DetailLabel>Confirmation Status</DetailLabel>
                            <DetailValue>
                                {tour.isConfirmedByGuide === null ? 'Pending' :
                                 tour.isConfirmedByGuide ? 'Confirmed' : 'Rejected'}
                                {tour.isConfirmedDate && ` (${moment(tour.isConfirmedDate).format('MMM D, YYYY')})`}
                            </DetailValue>
                        </DetailItem>
                    )}
                </TourDetails>
            </TourCard>
        );
    };

    return (
        <>
            <CalendarContainer>
                <DnDCalendar
                    date={calendarDate}
                    defaultView="month"
                    events={events}
                    localizer={localizer}
                    onSelectEvent={handleSelectEvent}
                    onNavigate={handleNavigate}
                    eventPropGetter={(event: any) => ({
                        style: getEventStyle(event.resource)
                    })}
                    style={{ height: "100%" }}
                />
            </CalendarContainer>

            {showConfirmationLegend && (
                <Legend>
                    <LegendItem>
                        <LegendSwatch $color="#4CAF50" />
                        Confirmed
                    </LegendItem>
                    <LegendItem>
                        <LegendSwatch $color="#FFF3E0" $dashed />
                        Awaiting approval
                    </LegendItem>
                </Legend>
            )}

            <AppModal isOpen={showTourModal} onClose={() => setShowTourModal(false)} className={null}>
                {selectedTour && (renderTourCard
                    ? renderTourCard(selectedTour, { closeModal: () => setShowTourModal(false) })
                    : defaultRenderTourCard(selectedTour))}
            </AppModal>
        </>
    );
};

export default TourCalendar; 